'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { cn } from '@/lib/utils';
import { fadeInUp } from '@/lib/animations';

interface SectionHeadingProps {
  subtitle?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  dark?: boolean;
  size?: 'default' | 'large';
  className?: string;
}

export function SectionHeading({
  subtitle,
  title,
  description,
  align = 'center',
  dark = false,
  size = 'default',
  className,
}: SectionHeadingProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      variants={fadeInUp}
      initial="initial"
      animate={isInView ? 'animate' : 'initial'}
      className={cn(
        'mb-12 md:mb-16',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {subtitle && (
        <p
          className={cn(
            'text-subtitle mb-4',
            size === 'large' ? 'text-base md:text-lg' : 'text-sm',
            dark ? 'text-zibarit-marron-secundario' : 'text-zibarit-verde-oscuro',
          )}
        >
          {subtitle}
        </p>
      )}
      <h2
        className={cn(
          'text-title whitespace-pre-line',
          size === 'large' ? 'text-4xl md:text-5xl lg:text-6xl' : 'text-3xl md:text-4xl lg:text-5xl',
          dark ? 'text-zibarit-bone' : 'text-zibarit-negro',
        )}
      >
        {title}
      </h2>
      {description && (
        <p
          className={cn(
            'text-body mt-4 max-w-2xl',
            align === 'center' && 'mx-auto',
            dark ? 'text-zibarit-marron-secundario' : 'text-zibarit-marron-oscuro',
          )}
        >
          {description}
        </p>
      )}
    </motion.div>
  );
}
